import React, { useRef } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import { OutboundLink } from "gatsby-plugin-google-gtag"
import SimpleText from "components/SimpleText"
import PageMetadata from "components/PageMetadata"
import useElementDimensions from "hooks/useElementDimensions"
import theme from "theme"

const FaqPage = ({
  data: {
    page: { metadata, questions, links },
  },
}) => {
  const ref = useRef()
  const dimensions = useElementDimensions(ref)

  return (
    <div>
      <PageMetadata {...metadata} />

      <div
        css={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "auto 1fr auto",
          gap: "24px 85px",
          padding: 24,
          [theme.tablet]: {
            gridTemplateColumns: "1fr",
          },
        }}
      >
        <h1 css={theme.h1}>FAQ</h1>

        <div
          css={{
            gridColumn: "1 / 2",
            gridRow: "2 / 3",
            [theme.tablet]: {
              gridColumn: "auto",
              gridRow: "auto",
              padding: "0 24px 24px",
              marginLeft: -24,
              marginRight: -24,
              marginBottom: -24,
              borderBottom: `2px solid ${theme.black}`,
            },
          }}
        >
          <div
            ref={ref}
            css={{
              [`@media (min-height: ${(dimensions?.height || 0) + 48}px)`]: {
                position: "sticky",
                top: 24,
              },
            }}
          >
            <h2 css={{ ...theme.hxs, marginBottom: 16 }}>Contents</h2>

            {questions.map(({ _key, question }) => (
              <div key={_key}>
                <a href={`#q-${_key}`} css={{ textDecoration: "none" }}>
                  {question}
                </a>
              </div>
            ))}
          </div>
        </div>

        <div
          css={{
            gridColumn: "2 / 3",
            gridRow: "1 / -1",
            [theme.tablet]: { gridColumn: "auto", gridRow: "auto" },
          }}
        >
          {questions.map(({ _key, question, answer }) => (
            <div key={_key} id={`q-${_key}`} css={{ marginTop: 24 }}>
              <h3
                css={{
                  ...theme.hl,
                  position: "relative",
                  [theme.tablet]: theme.hm,
                }}
              >
                <svg
                  fill="none"
                  viewBox="0 0 37 37"
                  width={37}
                  css={{
                    marginLeft: -37 - 24,
                    marginRight: 24,
                    position: "absolute",
                    [theme.tablet]: { display: "none" },
                  }}
                >
                  <circle
                    cx="18.5"
                    cy="18.5"
                    r="17.5"
                    stroke="#000"
                    strokeWidth="2"
                  />
                  <path
                    fill="#000"
                    d="M17 21.4h2.4c0-1.2.3-1.8 1.5-2.8 2-1.6 3.1-3 3.1-4.9 0-3-2.4-4.7-5.4-4.7-3.4 0-5.6 2-5.6 5.5l2.5.4c0-2.5 1.1-3.7 3-3.7 1.7 0 2.9 1 2.9 2.6 0 1.4-1 2.3-2.2 3.2-1.3 1.1-2.2 2.1-2.2 4.4zm-.5 5.6h3.3v-3.3h-3.3V27z"
                  />
                </svg>
                {question}
              </h3>
              <SimpleText content={answer} />
            </div>
          ))}
        </div>

        <div
          css={{
            gridColumn: "1 / 2",
            gridRow: "3 / 4",
            [theme.tablet]: { gridColumn: "auto", gridRow: "auto" },
          }}
        >
          <h2 css={theme.h1}>{links.title}</h2>

          <div
            css={{
              ...theme.hl,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              marginTop: 32,
              [theme.tablet]: theme.hm,
            }}
          >
            {links.links.map(({ _key, text, url }) => (
              <OutboundLink
                key={_key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                css={{ marginBottom: 16 }}
              >
                {text} ↗
              </OutboundLink>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FaqPage

FaqPage.propTypes = {
  data: PropTypes.shape({
    page: PropTypes.shape({
      metadata: PropTypes.object.isRequired,
      questions: PropTypes.arrayOf(
        PropTypes.shape({
          question: PropTypes.string.isRequired,
          answer: PropTypes.array.isRequired,
        }).isRequired
      ).isRequired,
      links: PropTypes.shape({
        title: PropTypes.string.isRequired,
        links: PropTypes.arrayOf(
          PropTypes.shape({
            _key: PropTypes.string.isRequired,
            text: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
          }).isRequired
        ).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
}

export const query = graphql`
  {
    page: sanityConfigSettings {
      metadata: faqMetadata {
        ...Metadata
      }
      questions: faqQuestions {
        _key
        question
        answer: _rawAnswer
      }
      links: faqLinks {
        title
        links {
          _key
          text
          url
        }
      }
    }
  }
`
